import { Component, type ErrorInfo, type ReactNode, StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { initMediaUpload } from "./media/mediaConfig";
import RootApp from "./Root";

declare global {
  interface Window {
    __rrReactRoot?: Root;
    /** Boot handshake for go.html status banner */
    __rrBoot?: { ok: true } | { ok: false; error: string };
  }
}

const MATCHING_KEY_PREFIX = "un8.matching.";

function clearMatchingStorage() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(MATCHING_KEY_PREFIX)) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

function bootOk() {
  window.__rrBoot = { ok: true };
  try {
    window.dispatchEvent(new CustomEvent("rr-boot", { detail: window.__rrBoot }));
  } catch {
    /* ignore */
  }
}

function bootFail(err: unknown) {
  const message = err instanceof Error ? err.message : String(err);
  window.__rrBoot = { ok: false, error: message };
  console.error("[UN8] boot failed", err);
  try {
    window.dispatchEvent(new CustomEvent("rr-boot", { detail: window.__rrBoot }));
  } catch {
    /* ignore */
  }
  const el = document.getElementById("root");
  if (el && el.childElementCount === 0) {
    el.innerHTML =
      '<div style="padding:2rem;max-width:28rem;margin:2rem auto;font-family:system-ui;line-height:1.45;color:#3a2a22">' +
      "<p>UN8 could not start: <strong>" +
      message.replace(/</g, "&lt;") +
      "</strong></p>" +
      '<p style="margin-top:1rem">If matching keeps failing, reset your local matching data and return to onboarding.</p>' +
      '<p style="margin-top:1rem"><button type="button" id="rr-clear-matching" style="padding:0.6rem 1rem;background:#8b1e2d;color:#fff;border:0;border-radius:6px;cursor:pointer">Clear matching data &amp; continue</button></p>' +
      "</div>";
    const btn = document.getElementById("rr-clear-matching");
    if (btn) {
      btn.addEventListener("click", () => {
        clearMatchingStorage();
        window.location.hash = "#/";
        window.location.reload();
      });
    }
  }
}

class BootErrorBoundary extends Component<
  { children: ReactNode },
  { error: string | null }
> {
  state: { error: string | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error: error?.message || String(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    bootFail(error);
    console.error("[UN8] render error", error, info.componentStack);
  }

  clearAndContinue = () => {
    clearMatchingStorage();
    window.location.hash = "#/";
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: "2rem",
            maxWidth: "28rem",
            margin: "2rem auto",
            fontFamily: "system-ui",
            lineHeight: 1.45,
            color: "#3a2a22",
          }}
        >
          <p>
            UN8 hit a render error: <strong>{this.state.error}</strong>
          </p>
          <p style={{ marginTop: "1rem" }}>
            Reloading this page alone will not help if your saved matching session is what
            triggered the loop. Clear local matching data to get back to onboarding.
          </p>
          <p style={{ marginTop: "1rem" }}>
            <button
              type="button"
              onClick={this.clearAndContinue}
              style={{
                padding: "0.6rem 1rem",
                background: "#8b1e2d",
                color: "#fff",
                border: 0,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Clear matching data & continue
            </button>
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

try {
  initMediaUpload();
} catch (err) {
  console.error("[UN8] initMediaUpload failed", err);
}

function ensureRootEl(): HTMLElement {
  let el = document.getElementById("root");
  if (!el) {
    el = document.createElement("div");
    el.id = "root";
    document.body.appendChild(el);
  }
  return el;
}

function mountApp() {
  const el = ensureRootEl();

  // Never createRoot twice on #root — a second root wipes the first (flash → beige blank).
  if (window.__rrReactRoot) {
    bootOk();
    return;
  }

  window.__rrReactRoot = createRoot(el);
  window.__rrReactRoot.render(
    <StrictMode>
      <BootErrorBoundary>
        <RootApp />
      </BootErrorBoundary>
    </StrictMode>
  );
  bootOk();
}

try {
  mountApp();
} catch (err) {
  bootFail(err);
}

// Unregister stale shell SWs only — keep push-sw.js for likes/matches.
window.addEventListener("load", () => {
  if (!("serviceWorker" in navigator)) return;
  window.setTimeout(() => {
    void navigator.serviceWorker.getRegistrations().then((regs) =>
      Promise.all(
        regs.map((r) => {
          const url =
            r.active?.scriptURL || r.waiting?.scriptURL || r.installing?.scriptURL || "";
          if (/push-sw\.js|firebase-messaging/i.test(url)) return Promise.resolve(false);
          return r.unregister();
        })
      )
    );
  }, 3000);
});
